import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import billingService from '../../services/billingService';
import HelpIcon from '../../components/help/HelpIcon';

const severityColor = (s) => ({
  CRITICAL: 'error',
  WARNING: 'warning',
  INFO: 'info',
}[s] || 'default');

/**
 * Shows the consistency report — a checklist of structural drift in
 * the operator\'s own state. Findings are categorized + severity-coded
 * so the operator can triage top-down.
 */
const ClickHouseConsistencyDialog = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await billingService.consistencyReport();
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Load failed');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const findings = useMemo(
    () => Array.isArray(data?.findings) ? data.findings : [],
    [data]);
  const byCategory = useMemo(() => {
    const m = new Map();
    findings.forEach(f => {
      const k = f.category || 'OTHER';
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(f);
    });
    return m;
  }, [findings]);

  const sev = data?.bySeverity || {};
  const criticalCount = sev.CRITICAL || 0;
  const warningCount = sev.WARNING || 0;
  const infoCount = sev.INFO || 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          Operator state consistency report
          <HelpIcon docKey="consistency" />
          <Box sx={{ flex: 1 }} />
          <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {data && (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="subtitle2">Summary:</Typography>
              {criticalCount > 0 && <Chip size="small" color="error" label={`${criticalCount} critical`} />}
              {warningCount > 0 && <Chip size="small" color="warning" label={`${warningCount} warning`} />}
              {infoCount > 0 && <Chip size="small" color="info" label={`${infoCount} info`} />}
              {findings.length === 0 && (
                <Chip size="small" color="success" label="No drift detected" />
              )}
            </Stack>

            {findings.length === 0 ? (
              <Alert severity="success">
                Operator configuration is coherent. Every node sits in a valid shard/replica slot,
                every tenant routes to a configured cluster, every retention policy targets a supported table.
              </Alert>
            ) : (
              [...byCategory.entries()].sort().map(([category, list]) => (
                <Box key={category} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                    {category} ({list.length})
                  </Typography>
                  <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {list.map((f, i) => (
                      <ListItem key={i} alignItems="flex-start" sx={{
                        borderBottom: i < list.length - 1 ? 1 : 0,
                        borderColor: 'divider',
                      }}>
                        <ListItemText
                          primary={
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                              <Chip size="small" color={severityColor(f.severity)}
                                    label={f.severity}
                                    sx={{ fontSize: 10, height: 18, fontWeight: 700 }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {f.message}
                              </Typography>
                            </Stack>
                          }
                          secondary={
                            f.fix ? (
                              <Typography variant="caption" color="text.secondary">
                                <strong>Fix:</strong> {f.fix}
                                {f.target && <> · <code style={{ fontSize: '0.85em' }}>target={f.target}</code></>}
                              </Typography>
                            ) : null
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClickHouseConsistencyDialog;
